import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import PlaceControls from "./PlaceControls";
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Button,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { createAndDownloadExcel, generateExcelFilename } from "../../utils/excelExportUtils";
import { renderOnline, renderOffline } from "../../components/StatusChip";
import {
  getCurrentTenant,
  TENANT_PATH_CONFIG,
} from "../../config/tenantConfig";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getRegion } from "../../api/regionAPI";
import { getDevices, updateDevice } from "../../api/deviceAPI";
import { getNode, getOnlineNodes as getOnlineNodesAPI, updateNode, deleteNode as deleteNodeAPI } from "../../api/nodeAPI";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useInterval } from "../../hooks/useInterval";


const getTenantPath = () => {
  const currentTenant = getCurrentTenant();
  const projectCode = currentTenant?.projectCode || "DEFAULT";
  return TENANT_PATH_CONFIG[projectCode] || TENANT_PATH_CONFIG["DEFAULT"];
};

export const getColumns = (t, handleMenuClick, onlineIds) => {
  return [
    // {
    //   field: "idNode",
    //   headerName: t("places.dataGrid.id"),
    //   flex: 0.5,
    //   minWidth: 80,
    //   headerAlign: "center",
    //   align: "center",
    // },
    {
      flex: 1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleMenuClick(event, params.row);
            }}
          >
            <MoreHorizIcon />
          </IconButton>
        );
      },
    },
    {
      field: "status",
      headerName: t("places.dataGrid.status"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: [
        { value: "Online", label: t("places.online") || "Çevrimiçi" },
        { value: "Offline", label: t("places.offline") || "Çevrimdışı" },
      ],
      renderCell: (params) =>
        onlineIds.includes(params.row.idNode)
          ? renderOnline("Online", t)
          : renderOffline("Offline", t),
    },
    {
      field: "code",
      headerName: t("places.dataGrid.code"),
      flex: 2,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "name",
      headerName: t("places.dataGrid.name"),
      flex: 3,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "terminalName",
      headerName: t("places.dataGrid.terminalName"),
      flex: 2,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "address",
      headerName: t("places.dataGrid.address"),
      flex: 2,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "city",
      headerName: t("places.dataGrid.city"),
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "town",
      headerName: t("places.dataGrid.town"),
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group1",
      headerName: t("places.dataGrid.group1"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group2",
      headerName: t("places.dataGrid.group2"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "group3",
      headerName: t("places.dataGrid.group3"),
      flex: 1,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
    },
  ];
};

export default function PlacesDataGrid() {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);
  const [onlineNodeIds, setOnlineNodeIds] = useState([]);
  const [devices, setDevices] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Menu & dialog state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteCodeInput, setDeleteCodeInput] = useState("");
  const [regions, setRegions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  // Loading states for async actions
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const [formPlace, setFormPlace] = useState({});

  // Terminal assign/remove state
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [selectedAssignDeviceId, setSelectedAssignDeviceId] = useState(0);
  const [selectedRemoveDeviceId, setSelectedRemoveDeviceId] = useState(0);
  // Transfer dialog states removed (feature disabled)

  // Stable callbacks using useCallback
  const handleNodesSuccess = useCallback((result) => {
    setNodes(result || []);
  }, []);

  const handleOnlineNodesSuccess = useCallback((result) => {
    const ids = (result || []).map((node) => node.idNode);
    setOnlineNodeIds(ids);
  }, []);

  const { loading, error, execute: executeNodes, clearError } = useApiRequest({
    onSuccess: handleNodesSuccess
  });

  const { execute: executeOnlineNodes } = useApiRequest({
    showErrorInConsole: true,
    onSuccess: handleOnlineNodesSuccess
  });

  // Fetch nodes - stable reference
  const fetchNodes = useCallback(async () => {
    await executeNodes(getNode, {
      errorMessage: "Failed to fetch nodes"
    });
  }, [executeNodes]);

  // useRef for stable online nodes fetching
  const fetchOnlineNodesRef = useRef();
  fetchOnlineNodesRef.current = async () => {
    await executeOnlineNodes(getOnlineNodesAPI);
  };

  // Tüm verileri yükleme fonksiyonu
  const loadAllData = useCallback(async () => {
    try {
      await fetchNodes();
      await fetchOnlineNodesRef.current();

      const [devicesResponse, regionsData] = await Promise.all([
        getDevices(),
        getRegion()
      ]);
      setDevices(devicesResponse?.data || devicesResponse || []);
      setRegions(regionsData || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }, [fetchNodes]);

  // İlk yükleme
  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 30 seconds with manual controls
  const { pause, resume, reset } = useInterval(loadAllData, 30000, {
    immediate: false,
    enabled: true
  });

  // Handle pause/resume
  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      resume();
      setIsPaused(false);
    } else {
      pause();
      setIsPaused(true);
    }
  }, [isPaused, pause, resume]);

  // Handle manual refresh (resets the interval timer)
  const handleManualRefresh = useCallback(() => {
    loadAllData();
    reset();
  }, [loadAllData, reset]);

  // Hataları yalnızca toast (Snackbar) ile göster
  useEffect(() => {
    if (!error) return;
    setSnackbar({ open: true, message: error, severity: "error" });
    clearError();
  }, [error, clearError]);

  const rows = useMemo(() => {
    return nodes.map((node) => ({
      id: node.idNode,
      idNode: node.idNode,
      status: onlineNodeIds.includes(node.idNode) ? "Online" : "Offline",
      name: node.name,
      code: node.code,
      regionName: node.regionName || "Bölge Yok",
      address: node.address,
      city: node.city,
      town: node.town,
      group1: node.group1,
      group2: node.group2,
      group3: node.group3,
      terminalName:
        (devices || [])
          .filter((d) => Number(d.node_idNode) === Number(node.idNode))
          .map((d) => d.nameDevice || d.serialDevice || `Device ${d.idDevice}`)
          .join(", ") || "",
    }));
  }, [nodes, onlineNodeIds, devices]);

  const handleRowClick = (params) => {
    const tenantPath = getTenantPath();
    window.open(`/${tenantPath}/${params.row.idNode}`, "_blank");
    console.log(`Navigating to /${tenantPath}/${params.row.idNode}`);
  };

  // Menu open
  const handleMenuClick = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleExportToExcel = () => {
    const exportColumns = [
      { field: "status", headerName: t("places.dataGrid.status") },
      { field: "code", headerName: t("places.dataGrid.code") },
      { field: "name", headerName: t("places.dataGrid.name") },
      { field: "terminalName", headerName: t("places.dataGrid.terminalName") },
      { field: "address", headerName: t("places.dataGrid.address") },
      { field: "city", headerName: t("places.dataGrid.city") },
      { field: "town", headerName: t("places.dataGrid.town") },
      { field: "group1", headerName: t("places.dataGrid.group1") },
      { field: "group2", headerName: t("places.dataGrid.group2") },
      { field: "group3", headerName: t("places.dataGrid.group3") },
    ];
    const fileName = generateExcelFilename(t("places.excelPrefix") || "Otomatlar");
    const result = createAndDownloadExcel(rows, exportColumns, t("places.excelSheet") || "Otomatlar", fileName);
    if (result.success) {
      setSnackbar({ open: true, message: t("places.excelSuccess") || "Excel dosyası indirildi", severity: "success" });
    } else {
      setSnackbar({ open: true, message: t("places.excelError") || "Excel indirme hatası", severity: "error" });
    }
  };

  const assignedDevicesForSelected = useMemo(() => {
    if (!selectedRow) return [];
    const nodeId = selectedRow.idNode;
    return (devices || []).filter(
      (d) => Number(d.node_idNode) === Number(nodeId)
    );
  }, [devices, selectedRow]);

  const unassignedDevices = useMemo(() => {
    return (devices || []).filter((d) => d.node_idNode === null);
  }, [devices]);

  // Edit open
  const openEditDialog = () => {
    if (!selectedRow) return;
    const fullNode = nodes.find((n) => n.idNode === selectedRow.idNode) || {};
    setFormPlace({
      idNode: fullNode.idNode,
      name: fullNode.name || "",
      code: fullNode.code || "",
      address: fullNode.address || "",
      city: fullNode.city || "",
      town: fullNode.town || "",
      group1: fullNode.group1 || "",
      group2: fullNode.group2 || "",
      group3: fullNode.group3 || "",
      region_idRegion: fullNode.region_idRegion || 0,
      propStr1: fullNode.propStr1 || "",
      propStr2: fullNode.propStr2 || "",
      propStr3: fullNode.propStr3 || "",
      propStr4: fullNode.propStr4 || "",
      propStr5: fullNode.propStr5 || "",
      propInt1: fullNode.propInt1 || 0,
      propInt2: fullNode.propInt2 || 0,
      propInt3: fullNode.propInt3 || 0,
      propInt4: fullNode.propInt4 || 0,
      propInt5: fullNode.propInt5 || 0,
    });
    setOpenAddEditDialog(true);
    handleMenuClose();
  };
  const closeAddEditDialog = () => {
    setOpenAddEditDialog(false);
  };

  // Delete
  const openDeleteDialog = () => {
    setDeleteCodeInput("");
    setOpenDeleteConfirm(true);
    handleMenuClose();
  };
  const closeDeleteDialog = () => {
    setOpenDeleteConfirm(false);
    setDeleteCodeInput("");
  };

  // Submit Edit
  const handleSubmitPlace = async () => {
    if (isSaving) return; // prevent double submit
    // validation
    if (
      !formPlace.code ||
      !formPlace.name ||
      !formPlace.region_idRegion ||
      !formPlace.address ||
      !formPlace.city ||
      !formPlace.town
    ) {
      setSnackbar({
        open: true,
        message: "Tüm zorunlu alanları doldurunuz.",
        severity: "error",
      });
      return;
    }
    try {
      setIsSaving(true);
      const payload = {
        idNode: formPlace.idNode,
        name: formPlace.name,
        code: formPlace.code,
        address: formPlace.address === "" ? null : formPlace.address,
        city: formPlace.city === "" ? null : formPlace.city,
        town: formPlace.town === "" ? null : formPlace.town,
        group1: formPlace.group1 === "" ? null : formPlace.group1,
        group2: formPlace.group2 === "" ? null : formPlace.group2,
        group3: formPlace.group3 === "" ? null : formPlace.group3,
        region_idRegion: formPlace.region_idRegion,
        isdeleted: 0,
        propStr1: formPlace.propStr1 === "" ? null : formPlace.propStr1,
        propStr2: formPlace.propStr2 === "" ? null : formPlace.propStr2,
        propStr3: formPlace.propStr3 === "" ? null : formPlace.propStr3,
        propStr4: formPlace.propStr4 === "" ? null : formPlace.propStr4,
        propStr5: formPlace.propStr5 === "" ? null : formPlace.propStr5,
        propInt1: formPlace.propInt1 === 0 ? null : formPlace.propInt1,
        propInt2: formPlace.propInt2 === 0 ? null : formPlace.propInt2,
        propInt3: formPlace.propInt3 === 0 ? null : formPlace.propInt3,
        propInt4: formPlace.propInt4 === 0 ? null : formPlace.propInt4,
        propInt5: formPlace.propInt5 === 0 ? null : formPlace.propInt5,
      };
      await updateNode(payload);
      await fetchNodes();
      setSnackbar({
        open: true,
        message: t("places.messages.placeUpdated") || "Yer güncellendi",
        severity: "success",
      });
      setOpenAddEditDialog(false);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || t("places.messages.errorCreatingPlace") || "Hata",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (isDeleting) return;
    if (!selectedRow) return;
    // Ensure user typed the correct place code before deleting
    if ((deleteCodeInput || "").trim() !== (selectedRow.code || "").trim()) {
      setSnackbar({
        open: true,
        message:
          t("places.dialogs.deletePlace.codeMismatch") ||
          "Yer kodu eşleşmiyor.",
        severity: "error",
      });
      return;
    }
    try {
      setIsDeleting(true);
      await deleteNodeAPI(selectedRow.idNode);
      await fetchNodes();
      setSnackbar({
        open: true,
        message: t("places.messages.placeDeleted"),
        severity: "success",
      });
      setOpenDeleteConfirm(false);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || t("places.messages.errorDeletingPlace"),
        severity: "error",
      });
      setOpenDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  // Assign Terminal
  const openAssignTerminalDialog = () => {
    if (!selectedRow) return;
    setSelectedAssignDeviceId(0);
    setOpenAssignDialog(true);
    handleMenuClose();
  };
  const closeAssignTerminalDialog = () => setOpenAssignDialog(false);
  const handleConfirmAssign = async () => {
    if (isAssigning) return;
    try {
      setIsAssigning(true);
      const device = (devices || []).find(
        (d) => d.idDevice === selectedAssignDeviceId
      );
      if (!device) {
        setSnackbar({
          open: true,
          message:
            t("places.messages.errorAssigningTerminal") || "Terminal atanamadı",
          severity: "error",
        });
        return;
      }
      const payload = { ...device, node_idNode: selectedRow.idNode };
      await updateDevice(payload);
      const devicesResponse = await getDevices();
      setDevices(devicesResponse?.data || devicesResponse || []);
      setSnackbar({
        open: true,
        message: t("places.messages.terminalAssigned") || "Terminal atandı",
        severity: "success",
      });
      setOpenAssignDialog(false);
    } catch (e) {
      setSnackbar({
        open: true,
        message:
          e.message || t("places.messages.errorAssigningTerminal") || "Hata",
        severity: "error",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  // Remove Terminal
  const openRemoveTerminalDialog = () => {
    if (!selectedRow) return;
    const firstAssigned = assignedDevicesForSelected[0];
    setSelectedRemoveDeviceId(firstAssigned ? firstAssigned.idDevice : 0);
    setOpenRemoveDialog(true);
    handleMenuClose();
  };
  const closeRemoveTerminalDialog = () => setOpenRemoveDialog(false);
  const handleConfirmRemove = async () => {
    if (isRemoving) return;
    try {
      setIsRemoving(true);
      const device = (devices || []).find(
        (d) => d.idDevice === selectedRemoveDeviceId
      );
      if (!device) {
        setSnackbar({
          open: true,
          message:
            t("places.messages.errorRemovingTerminal") ||
            "Terminal kaldırılamadı",
          severity: "error",
        });
        return;
      }
      const payload = { ...device, node_idNode: null };
      await updateDevice(payload);
      const devicesResponse = await getDevices();
      setDevices(devicesResponse?.data || devicesResponse || []);
      setSnackbar({
        open: true,
        message: t("places.messages.terminalRemoved") || "Terminal kaldırıldı",
        severity: "success",
      });
      setOpenRemoveDialog(false);
    } catch (e) {
      setSnackbar({
        open: true,
        message:
          e.message || t("places.messages.errorRemovingTerminal") || "Hata",
        severity: "error",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
      {/* Control Panel with Statistics */}
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Add Place Button - Left side on desktop */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <PlaceControls onPlacesUpdate={fetchNodes} />
          </Box>

          {/* Statistics Cards */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1 }}
          >
            {/* Add Place Button - Top on mobile */}
            <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
              <PlaceControls onPlacesUpdate={fetchNodes} />
            </Box>
              <Stack direction="column" spacing={0.5} alignItems="center">
                <Box sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "rgba(25, 118, 210, 0.1)",
                  border: 1.5,
                  borderColor: "primary.main",
                  color: "primary.main",
                  minWidth: 60,
                  textAlign: "center"
                }}>
                  <Typography variant="h6" fontWeight="bold">
                    {rows.length}
                  </Typography>
                </Box>
                <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                  {t("places.total") || "Toplam"}
                </Typography>
              </Stack>

              <Stack direction="column" spacing={0.5} alignItems="center">
                <Box sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "rgba(46, 125, 50, 0.1)",
                  border: 1.5,
                  borderColor: "success.main",
                  color: "success.main",
                  minWidth: 60,
                  textAlign: "center"
                }}>
                  <Typography variant="h6" fontWeight="bold">
                    {rows.filter(r => r.status === "Online").length}
                  </Typography>
                </Box>
                <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                  {t("places.online") || "Online"}
                </Typography>
              </Stack>

              <Stack direction="column" spacing={0.5} alignItems="center">
                <Box sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "rgba(211, 47, 47, 0.1)",
                  border: 1.5,
                  borderColor: "error.main",
                  color: "error.main",
                  minWidth: 60,
                  textAlign: "center"
                }}>
                  <Typography variant="h6" fontWeight="bold">
                    {rows.filter(r => r.status === "Offline").length}
                  </Typography>
                </Box>
                <Typography variant="caption" fontSize="0.75rem" color="text.secondary">
                  {t("places.offline") || "Offline"}
                </Typography>
              </Stack>
            </Stack>

            {/* Controls */}
            <Stack
              direction="column"
              spacing={1}
              alignItems="center"
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              {lastRefresh && (
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  {t("lastRefresh") || "Son güncelleme"}: {lastRefresh.toLocaleTimeString()}
                </Typography>
              )}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 0 }}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <Button
                  onClick={handlePauseResume}
                  startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                  color={isPaused ? "success" : "warning"}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    borderRadius: { xs: 1, sm: 0 },
                    borderTopLeftRadius: { sm: 1 },
                    borderBottomLeftRadius: { sm: 1 },
                    borderRight: { sm: "none" }
                  }}
                >
                  {isPaused ? (t("resume") || "Devam") : (t("pause") || "Duraklat")}
                </Button>
                <Button
                  onClick={handleManualRefresh}
                  startIcon={<RefreshIcon />}
                  disabled={loading}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    borderRadius: { xs: 1, sm: 0 },
                    borderTopRightRadius: { sm: 1 },
                    borderBottomRightRadius: { sm: 1 },
                  }}
                >
                  {t("refresh") || "Yenile"}
                </Button>
              </Stack>
              <Button
                onClick={handleExportToExcel}
                startIcon={<FileDownloadIcon />}
                disabled={rows.length === 0}
                variant="contained"
                color="success"
                size="medium"
                sx={{ borderRadius: 2, color: "white", whiteSpace: "nowrap" }}
              >
                {t("places.exportExcel") || "Dışarı Aktar"}
              </Button>
            </Stack>
          </Stack>
        </Box>

      <Box sx={{ height: "calc(100% - 200px)", width: "100%", position: "relative" }}>
        {loading && rows.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {/* Sayfa üstü hata metni kaldırıldı; hatalar sadece Snackbar ile gösterilir */}
        <DataGrid
          rows={rows}
          columns={getColumns(t, handleMenuClick, onlineNodeIds)}
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
            sorting: {
              sortModel: [{ field: "status", sort: "desc" }],
            },
          }}
          loading={loading && rows.length === 0}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          sx={{
            // Vertically center all cell content
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              paddingTop: "6px",
              paddingBottom: "6px",
            },
            "& .MuiDataGrid-cellContent": {
              display: "flex",
              alignItems: "center",
            },
            // Vertically center column headers as well
            "& .MuiDataGrid-columnHeader": {
              display: "flex",
              alignItems: "center",
              paddingTop: "6px",
              paddingBottom: "6px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              alignItems: "center",
            },
            // Make sure icon buttons inside cells are centered too
            "& .MuiDataGrid-cell .MuiIconButton-root": {
              alignSelf: "center",
            },
          }}
        />
      </Box>

      {/* Row action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {/* <MenuItem
          onClick={openTransferTerminalDialog}
          disabled={
            !selectedRow ||
            !nodes ||
            nodes.length === 0 ||
            assignedDevicesForSelected.length === 0
          }
        >
          {t("places.menu.transferTerminal") || "Terminali Transfer Et"}
        </MenuItem> */}
        <MenuItem onClick={openAssignTerminalDialog} disabled={!selectedRow}>
          {t("places.menu.assignTerminal") || "Terminal Ata"}
        </MenuItem>
        <MenuItem
          onClick={openRemoveTerminalDialog}
          disabled={!selectedRow || assignedDevicesForSelected.length === 0}
        >
          {t("places.menu.removeTerminal") || "Terminali Kaldır"}
        </MenuItem>
        <MenuItem onClick={openEditDialog} disabled={!selectedRow}>
          {t("common.edit") || "Düzenle"}
        </MenuItem>
        <MenuItem onClick={openDeleteDialog} disabled={!selectedRow}>
          {t("common.delete")}
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openAddEditDialog}
        onClose={closeAddEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pb: 1 }}>
          {t("places.dialogs.editPlace.title") || "Yeri Güncelle"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, mt: 1 }}>
          <DialogContentText>
            {t("places.dialogs.editPlace.subtitle") || "Yeri güncelleyin"}
          </DialogContentText>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                placeholder={t("places.dialogs.addPlace.code")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.code}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, code: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                placeholder={t("places.dialogs.addPlace.name")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.name}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="region-select-label">Region</InputLabel>
                <Select
                  labelId="region-select-label"
                  value={formPlace.region_idRegion}
                  label="Region"
                  onChange={(e) =>
                    setFormPlace({
                      ...formPlace,
                      region_idRegion: Number(e.target.value),
                    })
                  }
                >
                  <MenuItem value={0} disabled>
                    {t("places.dialogs.addPlace.selectRegion") ||
                      "Bölge Seçiniz"}
                  </MenuItem>
                  {regions &&
                    regions.map((region) => (
                      <MenuItem key={region.idRegion} value={region.idRegion}>
                        {region.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                placeholder={t("places.dialogs.addPlace.address")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.address}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, address: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                placeholder={t("places.dialogs.addPlace.city")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.city}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, city: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                placeholder={t("places.dialogs.addPlace.town")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.town}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, town: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                placeholder={t("places.dialogs.addPlace.group1")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.group1}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, group1: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                placeholder={t("places.dialogs.addPlace.group2")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.group2}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, group2: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                placeholder={t("places.dialogs.addPlace.group3")}
                InputLabelProps={{ shrink: false }}
                type="text"
                fullWidth
                variant="outlined"
                value={formPlace.group3}
                onChange={(e) =>
                  setFormPlace({ ...formPlace, group3: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Ekstra Detaylar
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Str 1"
                        InputLabelProps={{ shrink: false }}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formPlace.propStr1}
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propStr1: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Str 2"
                        InputLabelProps={{ shrink: false }}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formPlace.propStr2}
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propStr2: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Str 3"
                        InputLabelProps={{ shrink: false }}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formPlace.propStr3}
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propStr3: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Str 4"
                        InputLabelProps={{ shrink: false }}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formPlace.propStr4}
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propStr4: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Str 5"
                        InputLabelProps={{ shrink: false }}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={formPlace.propStr5}
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propStr5: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        placeholder="Prop Int 1"
                        InputLabelProps={{ shrink: false }}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={
                          formPlace.propInt1 === 0 ? "" : formPlace.propInt1
                        }
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propInt1:
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        placeholder="Prop Int 2"
                        InputLabelProps={{ shrink: false }}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={
                          formPlace.propInt2 === 0 ? "" : formPlace.propInt2
                        }
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propInt2:
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        placeholder="Prop Int 3"
                        InputLabelProps={{ shrink: false }}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={
                          formPlace.propInt3 === 0 ? "" : formPlace.propInt3
                        }
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propInt3:
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Int 4"
                        InputLabelProps={{ shrink: false }}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={
                          formPlace.propInt4 === 0 ? "" : formPlace.propInt4
                        }
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propInt4:
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Prop Int 5"
                        InputLabelProps={{ shrink: false }}
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={
                          formPlace.propInt5 === 0 ? "" : formPlace.propInt5
                        }
                        onChange={(e) =>
                          setFormPlace({
                            ...formPlace,
                            propInt5:
                              e.target.value === ""
                                ? 0
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeAddEditDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmitPlace}
            color="primary"
            variant="contained"
            sx={{ borderRadius: 2, minWidth: 120 }}
            disabled={isSaving}
          >
            {isSaving ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.saving") || "Saving..."}</span>
              </Stack>
            ) : (
              t("common.save") || "Kaydet"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={openDeleteConfirm}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("places.dialogs.deletePlace.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("places.dialogs.deletePlace.confirmDelete") ||
              "Bu yeri silmek istediğinizden emin misiniz?"}
          </DialogContentText>
          <DialogContentText sx={{ mb: 1 }}>
            {t("places.dialogs.deletePlace.enterCodeToDelete") ||
              "Silmek için yer kodunu giriniz:"}{" "}
            <Box
              component="span"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                fontFamily:
                  'Monaco, Menlo, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            >
              {selectedRow?.code}
            </Box>
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            placeholder={t("places.dataGrid.code") || "Yer Kodu"}
            value={deleteCodeInput}
            onChange={(e) => setDeleteCodeInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={
              isDeleting ||
              !selectedRow ||
              (deleteCodeInput || "").trim() !==
                (selectedRow?.code || "").trim()
            }
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            {isDeleting ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.deleting") || "Deleting..."}</span>
              </Stack>
            ) : (
              t("common.delete")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 3000 : 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Assign Terminal Dialog */}
      <Dialog
        open={openAssignDialog}
        onClose={closeAssignTerminalDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("places.dialogs.assignTerminal.title") || "Terminal Ata"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="assign-device-select-label">
              {t("places.dialogs.assignTerminal.selectDevice") ||
                "Terminal Seçiniz"}
            </InputLabel>
            <Select
              labelId="assign-device-select-label"
              value={selectedAssignDeviceId}
              label={
                t("places.dialogs.assignTerminal.selectDevice") ||
                "Terminal Seçiniz"
              }
              onChange={(e) =>
                setSelectedAssignDeviceId(Number(e.target.value))
              }
            >
              <MenuItem value={0} disabled>
                {t("places.dialogs.assignTerminal.selectDevice") ||
                  "Terminal Seçiniz"}
              </MenuItem>
              {unassignedDevices.map((dev) => (
                <MenuItem key={dev.idDevice} value={dev.idDevice}>
                  {dev.serialDevice || dev.nameDevice || dev.idDevice}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeAssignTerminalDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmAssign}
            variant="contained"
            disabled={isAssigning || !selectedAssignDeviceId}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            {isAssigning ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.saving") || "Saving..."}</span>
              </Stack>
            ) : (
              t("common.save") || "Kaydet"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Terminal Dialog */}
      <Dialog
        open={openRemoveDialog}
        onClose={closeRemoveTerminalDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("places.dialogs.removeTerminal.title") || "Terminali Kaldır"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="remove-device-select-label">
              {t("places.dialogs.removeTerminal.selectDevice") ||
                "Kaldırılacak Terminal"}
            </InputLabel>
            <Select
              labelId="remove-device-select-label"
              value={selectedRemoveDeviceId}
              label={
                t("places.dialogs.removeTerminal.selectDevice") ||
                "Kaldırılacak Terminal"
              }
              onChange={(e) =>
                setSelectedRemoveDeviceId(Number(e.target.value))
              }
            >
              {assignedDevicesForSelected.length === 0 ? (
                <MenuItem value={0} disabled>
                  {t("places.dialogs.removeTerminal.none") ||
                    "Atanmış terminal yok"}
                </MenuItem>
              ) : (
                assignedDevicesForSelected.map((dev) => (
                  <MenuItem key={dev.idDevice} value={dev.idDevice}>
                    {dev.serialDevice || dev.nameDevice || dev.idDevice}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeRemoveTerminalDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="error"
            variant="contained"
            disabled={isRemoving || !selectedRemoveDeviceId}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            {isRemoving ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.deleting") || "Deleting..."}</span>
              </Stack>
            ) : (
              t("common.delete") || "Sil"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Terminal Dialog */}
      {/* <Dialog
        open={openTransferDialog}
        onClose={closeTransferTerminalDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("places.dialogs.transferTerminal.title") ||
            "Terminali Transfer Et"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="transfer-device-select-label">
                  {t("places.dialogs.transferTerminal.selectDevice") ||
                    "Terminal Seçiniz"}
                </InputLabel>
                <Select
                  labelId="transfer-device-select-label"
                  value={selectedTransferDeviceId}
                  label={
                    t("places.dialogs.transferTerminal.selectDevice") ||
                    "Terminal Seçiniz"
                  }
                  onChange={(e) =>
                    setSelectedTransferDeviceId(Number(e.target.value))
                  }
                >
                  <MenuItem value={0} disabled>
                    {t("places.dialogs.transferTerminal.selectDevice") ||
                      "Terminal Seçiniz"}
                  </MenuItem>
                  {assignedDevicesForSelected.map((dev) => (
                    <MenuItem key={dev.idDevice} value={dev.idDevice}>
                      {dev.serialDevice || dev.nameDevice || dev.idDevice}
                      {dev.node_idNode ? `  (Node ${dev.node_idNode})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="transfer-node-select-label">
                  {t("places.dialogs.transferTerminal.selectNode") ||
                    "Hedef Yer"}
                </InputLabel>
                <Select
                  labelId="transfer-node-select-label"
                  value={selectedTransferNodeId}
                  label={
                    t("places.dialogs.transferTerminal.selectNode") ||
                    "Hedef Yer"
                  }
                  onChange={(e) =>
                    setSelectedTransferNodeId(Number(e.target.value))
                  }
                >
                  <MenuItem value={0} disabled>
                    {t("places.dialogs.transferTerminal.selectNode") ||
                      "Hedef Yer"}
                  </MenuItem>
                  {(nodes || []).map((n) => (
                    <MenuItem key={n.idNode} value={n.idNode}>
                      {n.name} ({n.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeTransferTerminalDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmTransfer}
            variant="contained"
            disabled={!selectedTransferDeviceId || !selectedTransferNodeId}
            sx={{ borderRadius: 2 }}
          >
            {t("common.save") || "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog> */}
    </Box>
  );
}
